import React, {
  useCallback,
  useEffect,
  useState,
} from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  Paper,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import { useSearchParams } from "react-router-dom";

import {
  getDemoBookings,
  updateDemoBookingStatus,
} from "../../services/demoBookingService";

const STATUSES = [
  "PENDING",
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
  "NO_SHOW",
];

const EMAIL_STATUSES = [
  "CONFIRMED",
  "COMPLETED",
  "CANCELLED",
];

const ITEMS_PER_PAGE = 15;

const ScheduledDemos = () => {
    const [searchParams] = useSearchParams();

    const statusFromUrl =
      searchParams.get("status") || "";
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] =
    useState(0);

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [status, setStatus] = useState(
    STATUSES.includes(statusFromUrl)
      ? statusFromUrl
      : ""
  );

  useEffect(() => {
    const urlStatus =
      searchParams.get("status") || "";

    setStatus(
      STATUSES.includes(urlStatus)
        ? urlStatus
        : ""
    );

    setPage(1);
  }, [searchParams]);

  // Status confirmation dialog
  const [statusDialogOpen, setStatusDialogOpen] =
    useState(false);

  const [selectedBooking, setSelectedBooking] =
    useState(null);

  const [selectedStatus, setSelectedStatus] =
    useState("");

  const [updatingStatus, setUpdatingStatus] =
    useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchBookings = useCallback(async () => {
    try {
      setLoading(true);

      const response = await getDemoBookings({
        search: search || undefined,
        status: status || undefined,
        page,
        limit: ITEMS_PER_PAGE,
      });

      setBookings(response.data || []);

      setTotalPages(
        response.pagination?.totalPages || 1
      );

      setTotalBookings(
        response.pagination?.total || 0
      );
    } catch (error) {
      console.error(
        "Failed to fetch demo bookings:",
        error
      );
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchBookings();
    }, 400);

    return () => clearTimeout(timer);
  }, [fetchBookings]);

  // Dropdown change only opens popup
  const handleStatusSelect = (
    booking,
    newStatus
  ) => {
    if (newStatus === booking.status) {
      return;
    }

    setSelectedBooking(booking);
    setSelectedStatus(newStatus);
    setStatusDialogOpen(true);
  };

  const handleCloseStatusDialog = () => {
    if (updatingStatus) {
      return;
    }

    setStatusDialogOpen(false);
    setSelectedBooking(null);
    setSelectedStatus("");
  };

  // Actual DB update + backend email
  const handleConfirmStatusUpdate = async () => {
    if (
      !selectedBooking ||
      !selectedStatus
    ) {
      return;
    }

    try {
      setUpdatingStatus(true);

      const response =
        await updateDemoBookingStatus(
          selectedBooking.id,
          selectedStatus
        );

      setStatusDialogOpen(false);

      setSnackbar({
        open: true,
        message:
          response?.emailMessage ||
          response?.message ||
          "Demo booking status updated successfully.",
        severity:
          response?.emailSent === false &&
          EMAIL_STATUSES.includes(selectedStatus)
            ? "warning"
            : "success",
      });

      setSelectedBooking(null);
      setSelectedStatus("");

      await fetchBookings();
    } catch (error) {
      console.error(
        "Failed to update status:",
        error
      );

      setSnackbar({
        open: true,
        message:
          error?.response?.data?.message ||
          "Failed to update demo booking status.",
        severity: "error",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getStatusColor = (value) => {
    switch (value) {
      case "CONFIRMED":
        return "primary";

      case "COMPLETED":
        return "success";

      case "CANCELLED":
        return "error";

      case "NO_SHOW":
        return "warning";

      default:
        return "default";
    }
  };

  const willSendEmail =
    EMAIL_STATUSES.includes(selectedStatus);

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        fontWeight={700}
        sx={{ mb: 1 }}
      >
        Scheduled Demos
      </Typography>

      <Typography
        sx={{
          color: "#64748b",
          mb: 3,
        }}
      >
        View and manage all demo booking requests.
      </Typography>

      {/* Status update message */}
      {snackbar.open && (
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((prev) => ({
              ...prev,
              open: false,
            }))
          }
          sx={{
            mb: 2,
            borderRadius: 2,
            fontWeight: 600,
            alignItems: "center",
          }}
        >
          {snackbar.message}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          border: "1px solid #e5e7eb",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 280 }}
          />

          <Select
            size="small"
            displayEmpty
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">
              All Statuses
            </MenuItem>

            {STATUSES.map((item) => (
              <MenuItem
                key={item}
                value={item}
              >
                {item}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Paper>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Message</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                Update Status
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : bookings.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  align="center"
                  sx={{ py: 5 }}
                >
                  No demo bookings found.
                </TableCell>
              </TableRow>
            ) : (
              bookings.map((booking) => (
                <TableRow key={booking.id}>
                  <TableCell>
                    {booking.full_name}
                  </TableCell>

                  <TableCell>
                    {booking.email}
                  </TableCell>

                  <TableCell>
                    {booking.phone || "—"}
                  </TableCell>

                  <TableCell>
                    {booking.company_name || "—"}
                  </TableCell>

                  <TableCell
                    sx={{
                      maxWidth: 250,
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {booking.message || "—"}
                  </TableCell>

                  <TableCell>
                    {new Date(
                      booking.preferred_date
                    ).toLocaleDateString()}
                  </TableCell>

                  <TableCell>
                    {booking.preferred_time}
                  </TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      label={booking.status}
                      color={getStatusColor(
                        booking.status
                      )}
                    />
                  </TableCell>

                  <TableCell>
                    <Select
                      size="small"
                      value={booking.status}
                      onChange={(e) =>
                        handleStatusSelect(
                          booking,
                          e.target.value
                        )
                      }
                      sx={{ minWidth: 150 }}
                    >
                      {STATUSES.map((item) => (
                        <MenuItem
                          key={item}
                          value={item}
                        >
                          {item}
                        </MenuItem>
                      ))}
                    </Select>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalBookings > 0 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            px: 1,
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748b",
            }}
          >
            Showing{" "}
            {(page - 1) * ITEMS_PER_PAGE + 1}
            {" to "}
            {Math.min(
              page * ITEMS_PER_PAGE,
              totalBookings
            )}
            {" of "}
            {totalBookings} scheduled demos
          </Typography>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(event, value) => {
              setPage(value);
            }}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Status confirmation popup */}
      <Dialog
        open={statusDialogOpen}
        onClose={handleCloseStatusDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Confirm Status Update
        </DialogTitle>

        <DialogContent>
          <DialogContentText
            component="div"
          >
            <Typography
              sx={{
                color: "#475569",
                mb: 2,
              }}
            >
              Update demo booking status for{" "}
              <strong>
                {selectedBooking?.full_name}
              </strong>
              ?
            </Typography>

            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                backgroundColor: "#f8fafc",
                border: "1px solid #e2e8f0",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#64748b",
                  mb: 0.5,
                }}
              >
                New Status
              </Typography>

              <Typography
                sx={{
                  fontWeight: 700,
                  color: "#0f172a",
                }}
              >
                {selectedStatus}
              </Typography>
            </Box>

            {willSendEmail && (
              <Alert
                severity="info"
                sx={{ mt: 2 }}
              >
                Clicking Update Status will also
                send an email to{" "}
                <strong>
                  {selectedBooking?.email}
                </strong>
                .
              </Alert>
            )}

            {!willSendEmail && (
              <Alert
                severity="warning"
                sx={{ mt: 2 }}
              >
                No customer email will be sent
                for this status.
              </Alert>
            )}
          </DialogContentText>
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            pb: 3,
          }}
        >
          <Button
            onClick={handleCloseStatusDialog}
            disabled={updatingStatus}
            color="inherit"
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmStatusUpdate}
            disabled={updatingStatus}
            variant="contained"
          >
            {updatingStatus ? (
              <>
                <CircularProgress
                  size={18}
                  color="inherit"
                  sx={{ mr: 1 }}
                />
                Updating...
              </>
            ) : (
              "Update Status"
            )}
          </Button>
        </DialogActions>
      </Dialog>


    </Box>
  );
};

export default ScheduledDemos;